import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession } from '../services/stripeService';
import { getMemoryById, getDisplayUrl } from '../services/memoryService';
import { getUserProfile } from '../services/userProfileService';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../hooks/useNotification';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import './CheckoutPage.css';
import logger from '../utils/logger';

function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const { showError, showSuccess } = useNotification();
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [heightsByMemoryId, setHeightsByMemoryId] = useState({});
  const [displayUrls, setDisplayUrls] = useState({});

  const formatAud = (amount) => {
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
        Number(amount || 0)
      );
    } catch (_) {
      return `AUD ${Number(amount || 0).toFixed(2)}`;
    }
  };

  const loadDisplayUrls = async (cartItems) => {
    const urlPromises = cartItems.map(async (cartItem) => {
      if (cartItem.memories) {
        const memory = cartItem.memories;
        const imageUrl = memory.figurine_url || memory.photo_url;
        if (imageUrl) {
          try {
            const displayUrl = await getDisplayUrl(imageUrl);
            return { id: cartItem.id, displayUrl };
          } catch (error) {
            console.error('Error getting display URL for checkout item:', cartItem.id, error);
            return { id: cartItem.id, displayUrl: imageUrl };
          }
        }
      }
      return null;
    });

    const results = await Promise.all(urlPromises);
    const urlMap = {};
    results.forEach(result => {
      if (result) {
        urlMap[result.id] = result.displayUrl;
      }
    });
    setDisplayUrls(urlMap);
  };

  // Get cart items, selected delivery fee, and credit info from navigation state
  const cartItems = location.state?.cartItems || [];
  const selectedDeliveryFee = location.state?.selectedDeliveryFee || null;
  const creditApplied = location.state?.creditApplied || 0;
  const useCredit = location.state?.useCredit || false;

  useEffect(() => {
    // Redirect if no cart items
    if (cartItems.length === 0) {
      navigate('/cart');
    } else {
      // Load display URLs for cart items
      loadDisplayUrls(cartItems);
    }
  }, [cartItems, navigate]);

  // Fetch user profile and auto-fill shipping address
  useEffect(() => {
    const fetchUserProfile = async () => {
      console.log('fetchUserProfile called, isAuthenticated:', isAuthenticated, 'user:', user);
      if (!isAuthenticated || !user) {
        console.log('User not authenticated or no user, skipping profile fetch');
        return;
      }
      
      try {
        const { data: profile, error } = await getUserProfile();
        
        if (error) {
          logger.error('Error fetching user profile for checkout:', error);
          return;
        }
        
        // Auto-fill shipping address if available
        if (profile?.shipping_address) {
          logger.debug('Auto-filling shipping address from user profile:', profile.shipping_address);
          console.log('Auto-filling shipping address:', profile.shipping_address);
          setShippingAddress(profile.shipping_address);
        } else {
          console.log('No shipping address found in profile:', profile);
        }
      } catch (error) {
        logger.error('Error in fetchUserProfile:', error);
      }
    };

    fetchUserProfile();
  }, [isAuthenticated, user]);

  // Fetch latest heights from Supabase for each memory id in cart
  useEffect(() => {
    const fetchHeights = async () => {
      try {
        const ids = Array.from(
          new Set(
            cartItems
              .map((i) => i.memories?.id)
              .filter((id) => typeof id === 'string' && id.length > 0)
          )
        );
        if (ids.length === 0) return;

        const results = await Promise.allSettled(ids.map((id) => getMemoryById(id)));
        const map = {};
        results.forEach((res, idx) => {
          if (res.status === 'fulfilled' && res.value) {
            map[ids[idx]] = Number(res.value.height) || 75;
          }
        });
        if (Object.keys(map).length > 0) setHeightsByMemoryId(map);
      } catch (_) {}
    };

    if (cartItems.length > 0) fetchHeights();
  }, [cartItems]);

  const handleStripeCheckout = async () => {
    // Require authentication before starting checkout
    if (!isAuthenticated) {
      navigate('/auth', { state: { redirectTo: '/checkout' } });
      return;
    }
    // Require confirmed email for purchase
    if (!user?.email_confirmed_at) {
      showError('Please confirm your email before completing a purchase.', {
        text: 'Resend email',
        onClick: async () => {
          try {
            const { resendEmailConfirmation } = await import('../services/supabase');
            const { error } = await resendEmailConfirmation(user.email);
            if (error && !(error.message || '').toLowerCase().includes('already registered')) {
              showError(`Failed to resend email: ${error.message}`);
            } else {
              showSuccess('Confirmation email resent. Please check your inbox.');
            }
          } catch (_) {}
        },
        style: { backgroundColor: '#4CAF50', color: 'white' },
      });
      return;
    }
    // Address is recommended but not strictly required for payment
    // We'll collect it for order fulfillment
    if (!shippingAddress) {
      setError('Please provide a shipping address for order fulfillment.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Create checkout session using the service
      const successUrl = `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/cart`;

      logger.debug('Creating checkout session with:', { successUrl, cancelUrl });

      // Generate signed URLs for images before creating checkout session
      const itemsWithSignedUrls = await Promise.all(
        cartItems.map(async (item) => {
          const imageUrl = item.memories?.figurine_url || item.memories?.photo_url;
          let signedImageUrl = null;
          
          if (imageUrl) {
            try {
              signedImageUrl = await getDisplayUrl(imageUrl);
              logger.debug('Generated signed URL for image:', { original: imageUrl, signed: signedImageUrl });
            } catch (error) {
              logger.warn('Failed to generate signed URL for image:', error);
              // Fallback to original URL if signing fails
              signedImageUrl = imageUrl;
            }
          }

          return {
            id: item.memories?.id || item.id,
            name: item.memories?.title || item.name || 'Memory',
            price: Number(item.price),
            quantity: Number(item.quantity) || 1,
            height:
              (item.memories?.id && heightsByMemoryId[item.memories.id]) ||
              Number(item.memories?.height) ||
              75,
            description: item.memories?.description || item.description || '',
            images: signedImageUrl ? [signedImageUrl] : [],
          };
        })
      );

      const { sessionId, url } = await createCheckoutSession(
        itemsWithSignedUrls,
        successUrl,
        cancelUrl,
        shippingAddress,
        selectedDeliveryFee?.fee || 0,
        creditApplied
      );

      logger.debug('Checkout session created:', { sessionId, url });

      // Redirect to Stripe checkout
      if (url) {
        window.location.href = url;
      } else {
        // Fallback: use Stripe.js redirect
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        const { error } = await stripe.redirectToCheckout({ sessionId });

        if (error) {
          throw error;
        }
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const calculateFinalTotal = () => {
    const subtotal = getTotalPrice();
    const deliveryFee = selectedDeliveryFee?.fee || 0;
    const totalBeforeCredit = subtotal + deliveryFee;
    
    if (useCredit && creditApplied > 0) {
      return Math.max(0, totalBeforeCredit - creditApplied);
    }
    
    return totalBeforeCredit;
  };

  if (cartItems.length === 0) {
    return null; // Will redirect
  }

  return (
    <div className="checkout-page">
      <Navbar />

      <div className="content">
        <div className="checkout-page-inner">
          <div className="checkout-container">
            <div className="checkout-header">
              <h1>Checkout</h1>
              <p>Complete your purchase</p>
            </div>

            <div className="checkout-content">
              <div className="order-summary">
                <h3>Order Summary</h3>
                {cartItems.map((item) => (
                  <div key={item.id} className="checkout-item">
                    <div className="item-image">
                      <img
                        src={
                          displayUrls[item.id] ||
                          item.memories?.figurine_url ||
                          item.memories?.photo_url ||
                          '/placeholder-image.png'
                        }
                        alt={item.memories?.title || 'Memory'}
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = '/placeholder-image.png';
                        }}
                      />
                    </div>
                    <div className="item-details">
                      <div className="item-title-price">
                        <h3>{item.memories?.title || item.name || 'Memory'}</h3>
                        <div className="item-price">{formatAud(item.price * item.quantity)}</div>
                      </div>
                      <div className="quantity-row">
                        <span className="quantity">Quantity: x{item.quantity}</span>
                        <span className="quantity"> | </span>
                        <span className="quantity">
                          Height: {(
                            (item.memories?.id && heightsByMemoryId[item.memories.id]) ||
                            Number(item.memories?.height) ||
                            75
                          )}mm
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="total-section">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>{formatAud(getTotalPrice())}</span>
                  </div>
                  <div className="total-row">
                    <span>Delivery ({selectedDeliveryFee?.name || 'Standard'}):</span>
                    <span>{formatAud(selectedDeliveryFee?.fee || 0)}</span>
                  </div>
                  {useCredit && creditApplied > 0 && (
                    <div className="total-row credit-row">
                      <span>Credit Applied:</span>
                      <span className="credit-deduction">-{formatAud(creditApplied)}</span>
                    </div>
                  )}
                  <div className="total-row total">
                    <span>Total:</span>
                    <span>{formatAud(calculateFinalTotal())}</span>
                  </div>
                </div>
              </div>

              <div className="shipping-section">
                <h3>Shipping</h3>
                <p>We currently ship within Australia only.</p>
                <AddressAutocomplete
                  value={shippingAddress}
                  onChange={setShippingAddress}
                  onValidChange={setIsAddressValid}
                  country="AU"
                  placeholder="Type your address (e.g. 123 George St, Sydney NSW)"
                />
              </div>

              <div className="payment-section">
                <h3>Payment</h3>
                <p>You'll be redirected to Stripe to complete your payment securely.</p>

                {error && <div className="error-message">{error}</div>}

                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handleStripeCheckout}
                  isLoading={loading}
                  disabled={!isAddressValid}
                >
                  {isAddressValid ? 'Pay with Stripe' : 'Enter valid shipping address'}
                </Button>

                <Button
                  variant="secondary"
                  size="md"
                  fullWidth
                  onClick={() => navigate('/cart')}
                  disabled={loading}
                >
                  Back to Cart
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
